import React from "react";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ISubscriptionPlan } from "../../../redux/subscription/subscription.types";
import { PRICING_ACCENT } from "../pricingTheme";

interface ITrialPlanBannerProps {
  plan: ISubscriptionPlan;
  /** True when the outlet is already on this trial plan. */
  isTrialActive?: boolean;
  loading?: boolean;
  onContinue: (plan: ISubscriptionPlan) => void;
}

/**
 * Builds a human readable trial duration label.
 * e.g. (3, "months") -> "3 months", (1, "month") -> "1 month".
 * Returns null when the period is unset (e.g. the -1 sentinel) so callers
 * can fall back to a generic label.
 */
const formatTrialLabel = (
  period?: number | null,
  cycle?: string | null,
): string | null => {
  const n = Number(period ?? 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = String(cycle ?? "months").replace(/s$/, ""); // month | day | week
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
};

export const TrialPlanBanner: React.FC<ITrialPlanBannerProps> = ({
  plan,
  isTrialActive = false,
  loading = false,
  onContinue,
}) => {
  const trialLabel = formatTrialLabel(plan.trial_period, plan.trial_cycle);

  return (
    <Box sx={{ mt: 5 }}>
      {/* "or" divider */}
      <Divider sx={{ "&::before, &::after": { borderColor: "divider" } }}>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 999,
            bgcolor: "action.hover",
            color: "text.secondary",
            fontWeight: 700,
            fontSize: "0.9rem",
          }}
        >
          or
        </Box>
      </Divider>

      <Paper
        variant="outlined"
        sx={{
          mt: 3,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          borderStyle: "dashed",
          borderWidth: 2,
          borderColor: PRICING_ACCENT.main,
          bgcolor: PRICING_ACCENT.soft,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#fff",
                color: PRICING_ACCENT.main,
              }}
            >
              <AutoAwesomeIcon />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                Not ready to commit?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Continue with a{" "}
                <Typography component="span" variant="body1" sx={{ fontWeight: 700, color: PRICING_ACCENT.main }}>
                  {trialLabel ?? "free"}
                </Typography>{" "}
                {trialLabel ? "free trial" : "trial"} — no payment required.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            size="large"
            disabled={loading || isTrialActive}
            onClick={() => onContinue(plan)}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 700,
              px: 4,
              borderRadius: 2,
              bgcolor: PRICING_ACCENT.main,
              color: "#fff",
              "&:hover": { bgcolor: PRICING_ACCENT.dark },
              "&.Mui-disabled": { bgcolor: PRICING_ACCENT.main, color: "#fff", opacity: 0.55 },
            }}
          >
            {isTrialActive ? "Trial active" : "Continue →"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default TrialPlanBanner;
